import React, { useEffect, useState } from 'react'
import { X, Trophy, Target, Calendar, ExternalLink } from 'lucide-react'
import { TeamFlairBadge } from './TeamFlairBadge'

interface PublicProfile {
  id: string
  displayName: string
  avatarUrl: string | null
  bio: string | null
  socialLinks: Record<string, string> | null
  favoriteTeamId: string | null
  createdAt: string
  stats: {
    totalBrackets: number
    totalPicks: number
    correctPicks: number
    accuracy: number
  }
}

interface UserProfileModalProps {
  userId: string | null
  onClose: () => void
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ userId, onClose }) => {
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    setError(null)
    setProfile(null)

    const apiBase = import.meta.env.VITE_API_URL || 'https://bracketblitz-production.up.railway.app/api/v1'
    fetch(`${apiBase}/users/${userId}/profile`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Profile not found')
        const json = await res.json()
        const p = json?.profile || json?.data?.profile || json
        setProfile({
          id: p.id,
          displayName: p.displayName || p.display_name || 'Player',
          avatarUrl: p.avatarUrl || p.avatar_url || null,
          bio: p.bio || null,
          socialLinks: p.socialLinks || p.social_links || null,
          favoriteTeamId: p.favoriteTeamId || p.favorite_team_id || null,
          createdAt: p.createdAt || p.created_at || '',
          stats: p.stats || { totalBrackets: 0, totalPicks: 0, correctPicks: 0, accuracy: 0 },
        })
      })
      .catch(() => setError('Could not load this profile'))
      .finally(() => setLoading(false))
  }, [userId])

  if (!userId) return null

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null

  const socialEntries = profile?.socialLinks
    ? Object.entries(profile.socialLinks).filter(([, v]) => v)
    : []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-sm rounded-2xl border border-white/[0.08] bg-dark-600/90 backdrop-blur-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/50 hover:text-white transition-all"
        >
          <X className="h-4 w-4" />
        </button>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-20 text-white/50 gap-2">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {profile && !loading && (
          <>
            {/* Header gradient */}
            <div className="h-20 bg-gradient-to-br from-primary-500/30 via-primary-600/20 to-accent-500/20" />

            {/* Avatar + Name */}
            <div className="px-6 -mt-10">
              <div className="flex items-end gap-4">
                <div className="relative">
                  {profile.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt={profile.displayName}
                      className="h-20 w-20 rounded-full border-4 border-dark-600 object-cover shadow-lg"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-full border-4 border-dark-600 bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg">
                      <span className="text-2xl font-bold text-white">
                        {profile.displayName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="pb-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-heading font-bold text-white truncate">
                      {profile.displayName}
                    </h2>
                    {profile.favoriteTeamId && (
                      <TeamFlairBadge teamId={profile.favoriteTeamId} size="sm" />
                    )}
                  </div>
                  {memberSince && (
                    <p className="text-xs text-white/40 flex items-center gap-1 mt-0.5">
                      <Calendar className="h-3 w-3" /> Member since {memberSince}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Bio */}
            {profile.bio && (
              <div className="px-6 mt-4">
                <p className="text-sm text-white/60 leading-relaxed">{profile.bio}</p>
              </div>
            )}

            {/* Stats grid */}
            <div className="px-6 mt-5 grid grid-cols-3 gap-3">
              <StatCard
                icon={<Trophy className="h-4 w-4 text-amber-400" />}
                value={profile.stats.totalBrackets}
                label="Brackets"
              />
              <StatCard
                icon={<Target className="h-4 w-4 text-green-400" />}
                value={`${profile.stats.accuracy}%`}
                label="Accuracy"
              />
              <StatCard
                icon={<span className="text-sm">🏀</span>}
                value={profile.stats.totalPicks}
                label="Picks"
              />
            </div>

            {/* Social links */}
            {socialEntries.length > 0 && (
              <div className="px-6 mt-4 flex flex-wrap gap-2">
                {socialEntries.map(([platform, url]) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-xs text-white/60 hover:text-white transition-all"
                  >
                    {platform}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
            )}

            {/* Bottom padding */}
            <div className="h-6" />
          </>
        )}
      </div>
    </div>
  )
}

const StatCard: React.FC<{ icon: React.ReactNode; value: string | number; label: string }> = ({
  icon, value, label,
}) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 text-center">
    <div className="flex justify-center mb-1">{icon}</div>
    <div className="text-lg font-bold text-white">{value}</div>
    <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
  </div>
)
